import { monitoring } from 'common/services';

import { PermissionsTelemetry } from './permissionsTelemetry';

import type { PermissionsScopeKind } from '../core';
import type { UnanswerableAttributes } from './permissionsTelemetry';

/**
 * One per session per scope kind, not one per gate.
 *
 * `useCan` is unmemoised and runs once per sidebar link, so every one of them sees the same
 * unanswerable snapshot. Module-scoped, which is the page load - the boundary Datadog samples on.
 */
const reported = new Set<PermissionsScopeKind>();

/** Test-only. The bound is module state by design, so a suite has to be able to clear it. */
export const resetUnanswerableReports = () => reported.clear();

/**
 * Report that the permissions query settled as unanswerable, so the squad's own monitor can fire.
 *
 * This exists because the service's SLO alerts structurally cannot cover it. A token-audience
 * mismatch, a blocked CORS preflight or a missing base URL either never reaches the service or is
 * answered correctly by it - the failure is entirely on this side of the wire, and nothing the
 * service owners watch will move. R19 asks for a frontend-side owner for exactly that gap, and this
 * is the facet the monitor stands on.
 *
 * Carries the scope kind, the HTTP status and the failure class, and nothing else - the same
 * constraint the transport's failure report follows, for the same reason: `monitoring` has already
 * keyed both Datadog stores to an identified user.
 */
export const reportUnanswerablePermissions = ({
  scopeKind,
  status,
  failureClass,
}: UnanswerableAttributes & { scopeKind: PermissionsScopeKind }): void => {
  if (reported.has(scopeKind)) return;

  reported.add(scopeKind);
  monitoring.logEvent(PermissionsTelemetry.unanswerable, {
    scopeKind,
    status,
    failureClass,
  } satisfies UnanswerableAttributes);
};
